/**
 * SF-5104: "Wanted by N companies" on an item, with a small popup listing them (hover, click, or
 * Enter/Space when focused). Shown only; companies play no part in the ranking.
 */
import { useState } from 'react';
import { Popover, Tag } from 'antd';
import type { PriorityCompany } from '../api/prioritiesClient';

const wantedByLabel = (count: number) =>
  `Wanted by ${count} ${count === 1 ? 'company' : 'companies'}`;

export const WantedByCompanies: React.FC<{ companies?: PriorityCompany[] }> = ({
  companies = [],
}) => {
  const [open, setOpen] = useState(false);
  if (companies.length === 0) return null;
  const label = wantedByLabel(companies.length);
  const names = companies
    .map((c) => c.name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return (
    <Popover
      title={label}
      trigger={['hover', 'click']}
      open={open}
      onOpenChange={setOpen}
      content={
        <ul
          aria-label={`Companies that want this`}
          style={{ margin: 0, paddingLeft: 18, maxHeight: 240, overflowY: 'auto' }}
        >
          {names.map((name, index) => (
            <li key={`${name}-${index}`}>{name}</li>
          ))}
        </ul>
      }
    >
      <Tag
        color="blue"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{ cursor: 'pointer' }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((value) => !value);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      >
        {label}
      </Tag>
    </Popover>
  );
};
